import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Briefcase, Loader2, Truck, Users, Activity, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Project {
  id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  primary_city: string | null;
  status: string;
  client_name: string | null;
  description: string | null;
  vehicle_count: number;
  driver_count: number;
  active_trip_count: number;
  mtd_revenue_sar: string;
}

const STATUS_TONE: Record<string, string> = {
  planning:  'bg-muted text-muted-foreground',
  active:    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  on_hold:   'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  completed: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  cancelled: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

const fmtSar = (s: string) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

export default function ProjectsListPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get<{ items: Project[] }>('/api/v1/projects')
      .then((r) => setItems(r.data.items))
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Briefcase className="h-4 w-4" /> {t('projects.title', { defaultValue: 'Operational projects' })}</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
            {t('projects.empty', { defaultValue: 'No operational projects yet.' })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((p) => (
              <Link key={p.id} to={`/dashboard/projects/${p.id}`}
                className="rounded-lg border border-border p-4 hover:bg-muted/30 transition-colors space-y-3 block">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{p.code}</div>
                    <div className="text-lg font-medium text-foreground">{p.name_en}</div>
                    {p.name_ar && <div className="text-xs text-muted-foreground" dir="rtl">{p.name_ar}</div>}
                  </div>
                  <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[p.status] ?? '')}>{p.status}</span>
                </div>
                {p.primary_city && (
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.primary_city}</div>
                )}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><Truck className="h-3 w-3" /> veh</div><div className="text-sm font-medium">{p.vehicle_count}</div></div>
                  <div><div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><Users className="h-3 w-3" /> drv</div><div className="text-sm font-medium">{p.driver_count}</div></div>
                  <div><div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><Activity className="h-3 w-3" /> trips</div><div className="text-sm font-medium">{p.active_trip_count}</div></div>
                  <div><div className="text-xs text-muted-foreground">MTD rev</div><div className="text-sm font-medium">{fmtSar(p.mtd_revenue_sar)}</div></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
